import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { authApi } from './authApi';
import { captureUserLoggedIn, resetPostHog } from '../../lib/posthog';

interface AuthState {
  user: {
    id: string;
    companyId: string;
    name: string;
    email: string;
    profile_image?: string;
    company?: {
      id: string;
      name: string;
      logo?: string;
    };
  } | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
}

const storedUser = localStorage.getItem('user');
const storedAccessToken = localStorage.getItem('accessToken');
const storedRefreshToken = localStorage.getItem('refreshToken');

const initialState: AuthState = {
  user: storedUser ? JSON.parse(storedUser) : null,
  accessToken: storedAccessToken ? storedAccessToken : null,
  refreshToken: storedRefreshToken ? storedRefreshToken : null,
  isAuthenticated: !!storedAccessToken,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{
        user: AuthState['user'];
        accessToken: string;
        refreshToken: string;
      }>
    ) => {
      state.user = action.payload.user;
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;

      localStorage.setItem('user', JSON.stringify(action.payload.user));
      localStorage.setItem('accessToken', action.payload.accessToken);
      localStorage.setItem('refreshToken', action.payload.refreshToken);
    },
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;

      localStorage.removeItem('user');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('token'); // Cleanup old key

      // Reset PostHog session so the next user gets a fresh anonymous identity.
      resetPostHog();
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      authApi.endpoints.login.matchFulfilled,
      (state, { payload }) => {
        state.user = payload.data.user;
        state.accessToken = payload.data.tokens.access.token;
        state.refreshToken = payload.data.tokens.refresh.token;
        state.isAuthenticated = true;

        localStorage.setItem('user', JSON.stringify(payload.data.user));
        localStorage.setItem('accessToken', payload.data.tokens.access.token);
        localStorage.setItem('refreshToken', payload.data.tokens.refresh.token);

        // Identify user in PostHog immediately after a successful login API call.
        captureUserLoggedIn();
      }
    );
  },
});

export const { setCredentials, logout } = authSlice.actions;
export default authSlice.reducer;

